import Flickity from 'flickity';
import { getRTL, getBreakpoint } from './_utils';
import debounce from 'lodash/debounce';
import isPlainObject from 'lodash/isPlainObject';
import assign from 'lodash/assign';
import { a11yHideSlide, a11yShowSlide } from './carousel';

const baseClass = 'CenterFocusCarousel';
const activeCardClass = `${baseClass}-Card--active`;
const animateCardClass = `${baseClass}-Card--animate`;

const selectors = {
	carousel: `.${baseClass}`,
	carouselContainer: `.${baseClass}-container--desktop`,
	mobileContainer: `.${baseClass}-container--mobile`,
	slide: `.${baseClass}--slide`,
	activeSlide: `.${baseClass}--slide.is-selected`,
	carouselList: `.${baseClass}--list`,
	card: `.${baseClass}-Card`,
	activeCard: `.${baseClass}-Card--active`,
	currentSlideNumber: `.${baseClass}-pagination--currentSlideNumber`,
	desktopNav: `.${baseClass}-navigation--desktop`,
	mobileNav: `.${baseClass}-navigation--mobile`,
	nextButton: `.${baseClass}-pagination--next`,
	prevButton: `.${baseClass}-pagination--prev`,
};

let bp = getBreakpoint();
const isRTL = getRTL();
const direction = isRTL ? 'right' : 'left';

const initDesktopCenterFocusCarousel = ($cardCarousel, config) => {
	const $container = $cardCarousel.find(selectors.carouselContainer);
	let { carouselHeight, activeBasedHeight, slideConfig } = config;

	if (typeof carouselHeight === 'function') {
		carouselHeight = carouselHeight();
	}

	if (!activeBasedHeight) {
		carouselHeight += slideConfig.heightDiff;
	}
	let slideMargins = calculateSlideMargins(slideConfig);
	let inactiveWidth = isPlainObject(slideConfig.inactiveWidth)
		? slideConfig.inactiveWidth[bp]
		: slideConfig.inactiveWidth;

	$container.find(selectors.slide).css('height', carouselHeight);

	const $navigation = $cardCarousel.find(
		`${selectors.desktopNav} ${selectors.currentSlideNumber}`
	);
	$navigation.text(`1 ${isRTL ? '\\' : '/'} ${$navigation.data('numberOfCards')}`);

	const $list = $container.children(selectors.carouselList);
	const $carousel = $list.get(0);
	const options = {
		cellAlign: 'center',
		pageDots: false,
		prevNextButtons: false,
		rightToLeft: isRTL,
		wrapAround: true,
		selectedAttraction: 0.075,
		friction: 0.8,
		draggable: true,
		adaptiveHeight: false,
		resize: false,
		percentPosition: false,
		imagesLoaded: false,
	};

	let $initSlides = setupSlides($container, slideConfig);
	$container.addClass(`${baseClass}--flickity`);
	const flickity = new Flickity($carousel, options);

	$carousel.removeAttribute('tabIndex');
	let $initialActiveSlide = $container.find(selectors.activeSlide);
	a11yShowSlide($initialActiveSlide[0]);
	fixSlideMargins($container, 0, slideMargins);

	flickity.on('change', function (index) {
		let $slides = $container.find(selectors.slide);
		$slides.each((index, slide) => {
			a11yHideSlide(slide);
		});
		a11yShowSlide($slides[flickity.selectedIndex]);
		$navigation.text(
			`${$($slides[flickity.selectedIndex]).data('slideIndex') + 1} ${
				isRTL ? '\\' : '/'
			} ${$navigation.data('numberOfCards')}`
		);
	});

	let prevDragX = 0;
	flickity.on('dragMove', function () {
		if (Math.abs(this.dragStartPosition - this.dragX) >= inactiveWidth) {
			this.dragX = prevDragX;
		} else {
			prevDragX = this.dragX;
		}
	});

	flickity.on('staticClick', function (event, pointer, cell, index) {
		if (index !== flickity.selectedIndex) {
			if (isRTL) {
				flickity.select(
					pointer.pageX < $(window).innerWidth() / 2
						? flickity.selectedIndex + 1
						: flickity.selectedIndex - 1,
					true
				);
			} else {
				flickity.select(
					pointer.pageX > $(window).innerWidth() / 2
						? flickity.selectedIndex + 1
						: flickity.selectedIndex - 1,
					true
				);
			}
		}
	});

	$cardCarousel.find(selectors.nextButton).on('click', function () {
		flickity.select(flickity.selectedIndex + 1, true);
	});
	$cardCarousel.find(selectors.prevButton).on('click', function () {
		flickity.select(flickity.selectedIndex - 1, true);
	});

	flickity.on('scroll', function () {
		fixSlideMargins($container, flickity.selectedIndex, slideMargins);
	});

	$(window).on(
		'resize',
		debounce(
			function () {
				$container
					.find(selectors.slide)
					.removeClass('is-selected animated')
					.css(`margin-${direction}`, '');
			},
			{ leading: true, trailing: false },
			200
		)
	);

	$(window).on(
		'resize',
		debounce(
			function () {
				setupSlides($container, slideConfig, flickity);
				let $activeCard = $container.find(selectors.activeCard);
				$activeCard.parent().addClass('is-selected');
				bp = getBreakpoint();
				slideMargins = calculateSlideMargins(slideConfig);
				inactiveWidth = isPlainObject(slideConfig.inactiveWidth)
					? slideConfig.inactiveWidth[bp]
					: slideConfig.inactiveWidth;
				fixSlideMargins($container, flickity.selectedIndex, slideMargins);
			},
			{ leading: false, trailing: true },
			200
		)
	);

	return flickity;
};

const setupSlides = ($container, slideConfig, flickity) => {
	const screenWidth = $(window).innerWidth();
	let $slides = $container.find(selectors.slide);
	$slides.each((index, slide) => {
		a11yHideSlide(slide);
		$(slide).attr('data-slide-index', $(slide).data('slideIndex') || index);
	});
	let minCardsNeeded = Math.max(Math.ceil(screenWidth / $slides.innerWidth()) + 2, 10);

	if (screenWidth > $slides.innerWidth() * ($slides.length - 2)) {
		//need to copy cards until we have enough
		let timesToClone = Math.floor(minCardsNeeded / $slides.length);
		//but it should be one less since we already have that amount of cards
		for (let i = 0; i < timesToClone; i++) {
			let $clones = $slides.clone();
			//Fix lazyload if it starts loading while clone is created
			$clones.find('.lazyloading').removeClass('lazyloading').addClass('lazyload');
			$clones.find(selectors.activeCard).css('height', '').removeClass(activeCardClass);
			if (flickity) {
				flickity.append($clones);
				flickity.resize();
			} else {
				$container.children(selectors.carouselList).append($clones);
			}
		}
		$slides = $container.find(selectors.slide);
	} else if (flickity) {
		flickity.resize();
	}
	$slides.each(function (index) {
		$(this).attr('data-flickity-index', index);
	});

	return $slides;
};

const fixSlideMargins = ($container, index, margins) => {
	let prevMargin = isRTL ? margins.nextMargin : margins.prevMargin;
	let nextMargin = isRTL ? margins.prevMargin : margins.nextMargin;
	const screenWidth = $(window).innerWidth();
	const $slides = $container.find(selectors.slide);
	const $activeSlide = $container.find(`${selectors.slide}.is-selected`);
	$activeSlide.addClass('animated');
	const prevIndex = index === 0 ? $slides.length - 1 : index - 1;
	const nextIndex = index === $slides.length - 1 ? 0 : index + 1;
	const $prevSlide = $($slides.get(prevIndex));
	$prevSlide.addClass('animated').css(`margin-${direction}`, prevMargin);
	const $nextSlide = $($slides.get(nextIndex));
	$nextSlide.addClass('animated').css(`margin-${direction}`, nextMargin);
	$slides.each(function (i) {
		if (index !== i) {
			const rect = this.getBoundingClientRect();
			const rightPosition = rect['right'];
			const leftPosition = rect['left'];

			if (i !== prevIndex && i !== nextIndex) {
				if (rightPosition > screenWidth - rect.width / 2) {
					$(this).removeClass('animated');
				} else if (leftPosition < rect.width / 2) {
					$(this).removeClass('animated');
				}
			}

			if (
				i === (isRTL ? nextIndex : prevIndex) ||
				(rightPosition >= -(rect.width / 2) && rightPosition <= screenWidth / 2) ||
				leftPosition > screenWidth + rect.width / 2
			) {
				$(this).css(`margin-${direction}`, prevMargin);
			} else if (
				i === (isRTL ? prevIndex : nextIndex) ||
				(leftPosition <= screenWidth + rect.width / 2 && leftPosition >= screenWidth / 2) ||
				rightPosition < -(rect.width / 2)
			) {
				$(this).css(`margin-${direction}`, nextMargin);
			}
		} else {
			$(this).css(`margin-${direction}`, '');
		}
	});
};

const calculateSlideMargins = slideConfig => {
	const activeSlideWidth = isPlainObject(slideConfig.activeWidth)
		? slideConfig.activeWidth[bp]
		: slideConfig.activeWidth;
	const inactiveSlideWidth = isPlainObject(slideConfig.inactiveWidth)
		? slideConfig.inactiveWidth[bp]
		: slideConfig.inactiveWidth;
	const activeFullWidth = activeSlideWidth + slideConfig.spacing;
	const inactiveFullWidth = inactiveSlideWidth + slideConfig.spacing;

	return {
		prevMargin: (inactiveFullWidth - activeSlideWidth) / 2,
		nextMargin: (activeFullWidth - inactiveSlideWidth) / 2,
	};
};

const initMobileCenterFocusCarousel = ($cardCarousel, config) => {
	const $container = $cardCarousel.find(selectors.mobileContainer);
	const $navigation = $cardCarousel.find(
		`${selectors.mobileNav} ${selectors.currentSlideNumber}`
	);
	$navigation.text(`1 ${isRTL ? '\\' : '/'} ${$navigation.data('numberOfCards')}`);

	if (config.carouselHeight) {
		$container
			.find(selectors.slide)
			.css(
				'height',
				typeof config.carouselHeight === 'function' ? carouselHeight() : carouselHeight
			);
	}

	if ($container.length > 0) {
		let options = {
			cellAlign: 'center',
			pageDots: false,
			prevNextButtons: false,
			rightToLeft: isRTL,
			wrapAround: false,
			selectedAttraction: 0.05,
			friction: 0.8,
			draggable: true,
			adaptiveHeight: false,
			resize: false,
			percentPosition: false,
			imagesLoaded: false,
			contain: true,
		};

		if (config && config.flickity) {
			assign(options, config.flickity);
		}

		const $list = $container.children(selectors.carouselList);
		const $carousel = $list.get(0);
		$container.addClass(`${baseClass}--flickity`);
		const flickity = new Flickity($carousel, options);
		const $nextButton = $cardCarousel.find(selectors.nextButton);
		const $prevButton = $cardCarousel.find(selectors.prevButton);

		if (!config.carouselHeight) {
			$container.find(selectors.slide).css('height', '100%');
		}
		flickity.on('change', function () {
			$prevButton.removeClass('button-disabled');
			$nextButton.removeClass('button-disabled');
			if (flickity.selectedIndex === 0) {
				$prevButton.addClass('button-disabled');
			} else if (flickity.selectedIndex === flickity.slides.length - 1) {
				$nextButton.addClass('button-disabled');
			}
			$navigation.text(
				`${flickity.selectedIndex + 1} ${isRTL ? '\\' : '/'} ${$navigation.data(
					'numberOfCards'
				)}`
			);
		});
		$nextButton.on('click', function () {
			flickity.select(flickity.selectedIndex + 1);
		});
		$prevButton.on('click', function () {
			flickity.select(flickity.selectedIndex - 1);
		});

		$(window).on(
			'resize',
			debounce(function () {
				if (!config.carouselHeight) {
					$container.find(selectors.slide).css('height', '');
					flickity.resize();
					$container.find(selectors.slide).css('height', '100%');
				} else {
					flickity.resize();
				}
			}, 200)
		);
		return flickity;
	}
};

export const initCenterFocusCarousel = ($container, desktopConfig, mobileConfig) => {
	if (bp === 'small') {
		initMobileCenterFocusCarousel($container, mobileConfig);

		const initAfterResize = debounce(function () {
			if (getBreakpoint() !== 'small') {
				initDesktopCenterFocusCarousel($container, desktopConfig);
				$(window).off('resize', initAfterResize);
			}
		});
		$(window).on('resize', initAfterResize);
	} else {
		initDesktopCenterFocusCarousel($container, desktopConfig);

		const initAfterResize = debounce(function () {
			if (getBreakpoint() === 'small') {
				initMobileCenterFocusCarousel($container, mobileConfig);
				$(window).off('resize', initAfterResize);
			}
		});
		$(window).on('resize', initAfterResize);
	}
};
